import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { catchError, filter, switchMap, throwError } from 'rxjs';
import {
  isSkippedByRefreshTokenInterceptorContext,
  isUnauthorized,
  NavigatorService,
  Route,
} from '@web/common';
import { once } from '@web/utils';
import { RefreshTokenService } from '../http/services/refresh-token.service';
import { AuthorizationHeaderService } from '../http/services/authorization-header.service';

export type RefreshTokenConfig = {
  shouldSignOutAfterTokenRefreshError: boolean;
};

export const RefreshTokenInterceptor: (
  config: RefreshTokenConfig,
) => HttpInterceptorFn = (config: RefreshTokenConfig) => (request, next) => {
  const navigator = inject(NavigatorService);
  const requestHeader = inject(AuthorizationHeaderService);
  const refreshToken = inject(RefreshTokenService);
  const { shouldSignOutAfterTokenRefreshError } = config;

  if (isSkippedByRefreshTokenInterceptorContext(request)) {
    return next(request);
  }

  return next(request).pipe(
    catchError((error) => {
      if (!isUnauthorized(error)) {
        return throwError(() => error);
      }

      return refreshToken.refreshing$.pipe(
        once(),
        switchMap((refreshing) => {
          if (refreshing) {
            return refreshToken.refreshed$.pipe(
              filter(Boolean),
              once(),
              switchMap(() =>
                requestHeader
                  .appendAuthorizationHeader(request)
                  .pipe(switchMap((request) => next(request))),
              ),
            );
          }

          return refreshToken.refresh().pipe(
            switchMap(() => requestHeader.appendAuthorizationHeader(request)),
            switchMap((request) => next(request)),
            catchError((error) => {
              if (shouldSignOutAfterTokenRefreshError) {
                navigator.navigateByRouterLink((provider) =>
                  provider.routerLink(Route.SignOut),
                );
              }

              return throwError(() => error);
            }),
          );
        }),
      );
    }),
  );
};
